import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { request } from '../../config/helpers/axios-instance';
import ServiceDeliveryInvoice from './ServiceDeliveryInvoice';

// Service invoices are rendered in the browser, uploaded to Azure, and the row keeps only the
// blob NAME in additionalDetails.invoicePdf. The SAS read token expires in ~10 minutes, so the
// `url` the upload endpoint returns is never persisted — the backend re-signs it on read as
// paymentItem.invoicePdfUrl. See docs/superpowers/specs/2026-09-18-service-invoice-pdf-storage-design.md

export const SERVICE_INVOICE_TYPE = 'SERVICE';

export const isServicePaymentItem = (paymentItem) => paymentItem?.invoiceType === SERVICE_INVOICE_TYPE;

export const hasStoredInvoicePdf = (paymentItem) => Boolean(paymentItem?.additionalDetails?.invoicePdf);

export const getInvoiceFileName = (paymentItem) =>
    `${paymentItem?.invoiceType?.toLowerCase() || 'invoice'}-invoice-${paymentItem?.id || 'payment-item'}.pdf`;

/** What gets merged into additionalDetails once the blob exists. */
export const buildInvoiceAdditionalDetails = (paymentItem, blobName, now = new Date()) => ({
    invoicePdf: blobName,
    invoiceNumber: paymentItem?.additionalDetails?.invoiceNumber || `PI-${paymentItem?.id}`,
    invoicePdfGeneratedAt: now.toISOString(),
});

/**
 * How many A4 pages a captured canvas needs at the given page aspect. Exposed for tests; the
 * previous implementation squashed everything into one page with Math.min().
 */
export const countPdfPages = (canvasWidth, canvasHeight, pageWidthMm, pageHeightMm) => {
    if (!canvasWidth || !canvasHeight) return 1;
    const pageHeightPx = (canvasWidth * pageHeightMm) / pageWidthMm;
    return Math.max(1, Math.ceil(canvasHeight / pageHeightPx));
};

const waitForImages = async (root) => {
    const images = Array.from(root.querySelectorAll('img'));
    await Promise.all(
        images.map((img) =>
            img.complete
                ? Promise.resolve()
                : new Promise((resolve) => {
                    img.onload = resolve;
                    img.onerror = resolve; // a missing seal must not block the invoice
                })
        )
    );
};

/**
 * Render the invoice off-screen and return a jsPDF document. Mounts its own React root in a
 * fixed, off-viewport container so it works from any handler without component state/refs.
 */
export const renderServiceInvoicePdf = async (paymentItem, sourceData) => {
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-12000px;top:0;z-index:-1;';
    document.body.appendChild(host);
    const root = createRoot(host);

    try {
        flushSync(() => {
            root.render(React.createElement(ServiceDeliveryInvoice, { paymentItem, sourceData }));
        });
        await waitForImages(host);

        const canvas = await html2canvas(host.firstElementChild, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
        });

        const doc = new jsPDF('l', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const pages = countPdfPages(canvas.width, canvas.height, pageWidth, pageHeight);
        const pageHeightPx = (canvas.width * pageHeight) / pageWidth;

        // Slice the capture into page-height strips instead of scaling it to fit one page.
        for (let page = 0; page < pages; page += 1) {
            const sliceHeight = Math.min(pageHeightPx, canvas.height - page * pageHeightPx);
            const slice = document.createElement('canvas');
            slice.width = canvas.width;
            slice.height = sliceHeight;
            slice.getContext('2d').drawImage(
                canvas,
                0, page * pageHeightPx, canvas.width, sliceHeight,
                0, 0, canvas.width, sliceHeight
            );
            if (page > 0) doc.addPage();
            doc.addImage(
                slice.toDataURL('image/png'),
                'PNG',
                0, 0,
                pageWidth,
                (sliceHeight * pageWidth) / canvas.width
            );
        }
        return doc;
    } finally {
        root.unmount();
        host.remove();
    }
};

/** Upload the PDF blob; returns the Azure blob name or throws. */
export const uploadInvoicePdf = async (blob, fileName) => {
    const formData = new FormData();
    formData.append('files', new File([blob], fileName, { type: 'application/pdf' }));

    const res = await request({
        url: '/file/upload/prescription/',
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    const blobName = res?.data?.data?.[0]?.pdf;
    if (!res?.data?.success || !blobName) {
        throw new Error(res?.data?.error || 'Invoice upload failed');
    }
    return blobName;
};

/**
 * Render → upload → persist blob name on the payment item.
 * `paymentItem` must be the SERVER row (post-create/update): the backend rewrites the GST split
 * from the order address, so rendering from form values would print the wrong CGST/SGST/IGST.
 * Returns the blob name. Throws on any failure — callers decide how loud to be; the payment
 * item itself is never rolled back because of the PDF.
 */
export const persistServiceInvoicePdf = async (paymentItem, sourceData) => {
    const doc = await renderServiceInvoicePdf(paymentItem, sourceData);
    const blobName = await uploadInvoicePdf(doc.output('blob'), getInvoiceFileName(paymentItem));

    const res = await request({
        url: `paymentItem/admin/id/${paymentItem.id}`,
        method: 'PUT',
        data: { additionalDetails: buildInvoiceAdditionalDetails(paymentItem, blobName) },
    });
    if (!res?.data?.success) {
        throw new Error(res?.data?.message || res?.data?.error || 'Failed to save invoice reference');
    }
    return blobName;
};

/** Fresh server row — needed after an edit, since the PUT response is not the updated row. */
export const fetchPaymentItem = async (paymentItemId) => {
    const res = await request({ url: `paymentItem/admin/paymentItemDetails/${paymentItemId}`, method: 'GET' });
    if (!res?.data?.success || !res?.data?.data?.id) {
        throw new Error('Could not load payment item');
    }
    return res.data.data;
};
